import React, { useState, useCallback } from 'react';
import { Button, Slider, Upload, Space, Alert, Typography, App } from 'antd';
import { UploadOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import {
  createCroppedImage,
  extractBase64Data,
  getBase64FileSize,
  formatFileSize,
  readFileAsDataURL,
} from '../../utils/imageProcessing';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/user/AvatarUploadModal.tsx');

interface AvatarUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (avatarUrl: string) => void;
  currentAvatarUrl?: string | null;
  uploading?: boolean;
}

const MAX_FILE_SIZE = 500 * 1024; // 500KB
const TARGET_SIZE = 200; // 200x200px
const JPEG_QUALITY = 0.85;

export const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentAvatarUrl: _currentAvatarUrl,
  uploading = false,
}) => {
  const { message } = App.useApp();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleFileChange = async (file: File) => {
    // ファイルタイプチェック
    if (!file.type.startsWith('image/')) {
      message.error('画像ファイルを選択してください');
      return false;
    }

    // ファイルサイズチェック（元ファイル）
    if (file.size > 10 * 1024 * 1024) {
      message.error('ファイルサイズが大きすぎます（最大10MB）');
      return false;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      setImageSrc(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      message.error('画像の読み込みに失敗しました');
      console.error('File read error:', error);
    }

    return false; // Prevent default upload behavior
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      message.error('画像を選択してください');
      return;
    }

    setProcessing(true);
    try {
      // 画像を切り抜いてリサイズ
      const croppedImage = await createCroppedImage(
        imageSrc,
        croppedAreaPixels,
        TARGET_SIZE,
        JPEG_QUALITY
      );

      // ファイルサイズチェック
      const fileSize = getBase64FileSize(croppedImage);
      if (fileSize > MAX_FILE_SIZE) {
        message.error(
          `処理後の画像サイズが大きすぎます（${formatFileSize(fileSize)}、最大500KB）`
        );
        setProcessing(false);
        return;
      }

      // Base64データ部分のみを抽出
      const base64Data = extractBase64Data(croppedImage);

      // 成功コールバック
      onSuccess(base64Data);

      // モーダルを閉じる
      handleClose();
    } catch (error) {
      message.error('画像の処理に失敗しました');
      console.error('Image processing error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  };

  return (
    <>
      <Modal
        title="アバター画像を設定"
        open={open}
        onCancel={handleClose}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleClose} disabled={uploading || processing}>
            キャンセル
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={uploading || processing}
            disabled={!imageSrc || !croppedAreaPixels}
          >
            アップロード
          </Button>,
        ]}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {!imageSrc ? (
            <Upload.Dragger
              accept="image/*"
              beforeUpload={handleFileChange}
              showUploadList={false}
              multiple={false}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">クリックまたはドラッグして画像を選択</p>
              <p className="ant-upload-hint">
                JPG、PNG、WebP形式に対応（最大10MB）
              </p>
            </Upload.Dragger>
          ) : (
            <>
              <div style={{ position: 'relative', width: '100%', height: 400, background: '#f0f0f0' }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="round"
                  showGrid={false}
                />
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <ZoomOutOutlined />
                    <Text>ズーム</Text>
                  </Space>
                  <ZoomInOutlined />
                </Space>
                <Slider
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(value) => setZoom(value)}
                />
              </Space>

              <Alert
                message="画像を切り抜く範囲を調整してください"
                description={`切り抜いた画像は${TARGET_SIZE}x${TARGET_SIZE}pxにリサイズされ、JPEG形式で保存されます。`}
                type="info"
                showIcon
              />

              <Button
                block
                onClick={() => setImageSrc(null)}
                disabled={uploading || processing}
              >
                別の画像を選択
              </Button>
            </>
          )}
        </Space>
      </Modal>
    </>
  );
};
